/**
 * @file Hook for detecting reduced motion preference
 * @description 
 * This hook detects if the user has enabled the "prefers-reduced-motion" setting
 * in their operating system or browser. It allows components to adapt animations
 * based on user accessibility preferences.
 * 
 * Key features:
 * - Detects OS/browser reduced motion preference
 * - Updates in real-time when preference changes
 * - Improves accessibility for users sensitive to motion
 * - SSR compatible with safe default values
 * 
 * @dependencies
 * - React useState and useEffect for state management
 */

import { useState, useEffect } from "react";

/**
 * Hook to detect if the user prefers reduced motion
 * @returns Boolean indicating if reduced motion is preferred
 */
export function useReducedMotion() {
  // Default to false for SSR, assuming animation is acceptable
  // This prevents layout shifts when hydrating
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    // Create media query to detect prefers-reduced-motion setting
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    
    // Set initial state based on media query
    setPrefersReducedMotion(mediaQuery.matches);
    
    // Handler for when the preference changes
    const onChange = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    
    // Add event listener to detect changes in preference
    mediaQuery.addEventListener('change', onChange);
    
    // Clean up event listener on component unmount
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);
  
  return prefersReducedMotion;
}

/**
 * Hook to get optimized animation settings based on reduced motion preference
 * @returns Object with animation configuration based on motion preference
 */
export function useAnimationSettings() {
  const prefersReducedMotion = useReducedMotion();
  
  if (prefersReducedMotion) {
    return {
      // When reduced motion is preferred, disable animations or use minimal ones
      enabled: false,
      duration: 0,
      delay: 0,
      x: 0,
      y: 0,
      opacity: { from: 1, to: 1 },
    };
  }
  
  // Default animation settings when full motion is acceptable
  return {
    enabled: true,
    duration: 0.6,
    delay: 0.2,
    x: 20,
    y: 20,
    opacity: { from: 0, to: 1 },
  };
}